import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  calculateDistance,
  calculateGlideSlope,
  calculateVerticalSpeed,
} from './math';
import GlobalStyle from './styles/GlobalStyle';
import { theme } from './styles/theme';
import { I18nProvider } from './i18n/index';
import AppHeader from './components/AppHeader';
import AviationBackground from './components/AviationBackground';
import AircraftAnimation from './components/AircraftAnimation';
import CalculatorForm from './components/CalculatorForm';
import ResultsPanel from './components/ResultsPanel';
import InfoPanel from './components/InfoPanel';
import DisclaimerPanel from './components/DisclaimerPanel';
import AppFooter from './components/AppFooter';

// ── Pure parsing helpers ─────────────────────────────────────────────────────

const makeInt = (v, ifEmpty = '') => {
  const s = (typeof v === 'string' ? v : String(v ?? '')).replace(/\s+/g, '');
  return parseInt(s || String(ifEmpty), 10);
};

const makeFloat = (v, ifEmpty = '') => {
  const s = (typeof v === 'string' ? v : String(v ?? '')).replace(/\s+/g, '');
  return parseFloat(s || String(ifEmpty));
};

const parseForm = (f) => ({
  startAltitude: makeInt(f.startAltitude),
  endAltitude: makeInt(f.endAltitude),
  startSpeed: makeInt(f.startSpeed),
  endSpeed: makeInt(f.endSpeed),
  wind: makeInt(f.wind, 0),
  glideSlope: makeFloat(f.glideSlope),
  verticalSpeed: makeFloat(f.verticalSpeed),
});

// ── Initial state ────────────────────────────────────────────────────────────

const INITIAL_FORM = {
  startAltitude: '',
  endAltitude: '',
  startSpeed: '',
  endSpeed: '',
  glideSlope: '3',
  wind: '0',
  verticalSpeed: '',
};

// Pre-filled known-good example (30k→10k, 320/250 kts, GS 3°, VS 1513 fpm)
const EXAMPLE_FORM = {
  startAltitude: '30000',
  endAltitude: '10000',
  startSpeed: '320',
  endSpeed: '250',
  glideSlope: '3',
  wind: '0',
  verticalSpeed: '1513',
};

// ── Root App component (wrapped in I18nProvider below) ───────────────────────

const AppInner = () => {
  const [form, setForm] = useState(INITIAL_FORM);

  // Unified field change handler — updates the changed field and
  // recomputes the linked VS ↔ glideSlope pair in the same state update.
  const handleChange = useCallback((field, rawValue) => {
    setForm((prev) => {
      const next = { ...prev, [field]: rawValue };
      const p = parseForm(next);

      if (field === 'verticalSpeed') {
        const gs = calculateGlideSlope({
          startSpeed: p.startSpeed,
          endSpeed: p.endSpeed,
          wind: p.wind,
          verticalSpeed: p.verticalSpeed,
        });
        if (gs !== '') next.glideSlope = String(gs);
      } else {
        const vs = calculateVerticalSpeed({
          startSpeed: p.startSpeed,
          endSpeed: p.endSpeed,
          wind: p.wind,
          glideSlope: p.glideSlope,
        });
        if (vs !== '') next.verticalSpeed = String(vs);
      }

      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setForm(INITIAL_FORM);
  }, []);

  const handleFillExample = useCallback(() => {
    setForm(EXAMPLE_FORM);
  }, []);

  // Derived computed results — recalculate whenever form changes.
  const results = useMemo(() => {
    const p = parseForm(form);
    const { distance, estimated, errors } = calculateDistance(p);
    return {
      distance:
        distance !== undefined && distance !== '' ? String(distance) : '',
      estimated:
        estimated !== undefined && estimated !== '' ? String(estimated) : '',
      errors: errors || {},
    };
  }, [form]);

  return (
    <>
      <GlobalStyle />
      <AviationBackground />
      <AircraftAnimation />
      <AppHeader />
      <Main>
        <ContentArea>
          <CalculatorForm
            form={form}
            onFieldChange={handleChange}
            onReset={handleReset}
            onFillExample={handleFillExample}
            errors={results.errors}
          />
          <ResultsPanel
            distance={results.distance}
            estimated={results.estimated}
            glideSlope={form.glideSlope}
            startAltitude={form.startAltitude}
            endAltitude={form.endAltitude}
          />
          <InfoPanel />
          <DisclaimerPanel />
        </ContentArea>
      </Main>
      <AppFooter />
    </>
  );
};

// ── Exported App — provides i18n context ─────────────────────────────────────

const App = () => (
  <I18nProvider defaultLocale="uz">
    <AppInner />
  </I18nProvider>
);

// ── Layout ────────────────────────────────────────────────────────────────────

const Main = styled.main`
  flex: 1;
  padding: ${theme.spacing.xl};
  position: relative;
  z-index: 5;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
  }
`;

const ContentArea = styled.div`
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

export default App;
